import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import SectionNavigator from '../../components/ui/SectionNavigator';
import CustomizationPanel from '../../components/ui/CustomizationPanel';
import ExportActionBar from '../../components/ui/ExportActionBar';
import ContentTypeSelector from './components/ContentTypeSelector';
import TextInput from './components/TextInput';
import URLInput from './components/URLInput';
import WiFiInput from './components/WiFiInput';
import ContactInput from './components/ContactInput';
import EmailInput from './components/EmailInput';
import SMSInput from './components/SMSInput';
import QRPreview from './components/QRPreview';

const QRCodeGenerator = () => {
  const [contentType, setContentType] = useState('text');
  const [textContent, setTextContent] = useState('');
  const [urlContent, setUrlContent] = useState('');
  const [wifiContent, setWifiContent] = useState({
    ssid: '',
    password: '',
    security: 'WPA'
  });
  const [contactContent, setContactContent] = useState({
    name: '',
    phone: '',
    email: '',
    company: '',
    address: ''
  });
  const [emailContent, setEmailContent] = useState({
    recipient: '',
    subject: '',
    body: ''
  });
  const [smsContent, setSmsContent] = useState({
    phone: '',
    message: ''
  });

  const [qrSize, setQrSize] = useState(400);
  const [foregroundColor, setForegroundColor] = useState('#1E293B');
  const [backgroundColor, setBackgroundColor] = useState('#FFFFFF');
  const [errorCorrection, setErrorCorrection] = useState('M');
  const [customizationExpanded, setCustomizationExpanded] = useState(false);
  const [lastQRCode, setLastQRCode] = useState(null);

  const generateQRData = () => {
    switch (contentType) {
      case 'text':
        return textContent;
      case 'url':
        return urlContent;
      case 'wifi':
        return `WIFI:T:${wifiContent?.security};S:${wifiContent?.ssid};P:${wifiContent?.password};;`;
      case 'contact':
        return `BEGIN:VCARD\nVERSION:3.0\nFN:${contactContent?.name}\nTEL:${contactContent?.phone}\nEMAIL:${contactContent?.email}\nORG:${contactContent?.company}\nADR:${contactContent?.address}\nEND:VCARD`;
      case 'email':
        return `mailto:${emailContent?.recipient}?subject=${encodeURIComponent(emailContent?.subject)}&body=${encodeURIComponent(emailContent?.body)}`;
      case 'sms':
        return `sms:${smsContent?.phone}?body=${encodeURIComponent(smsContent?.message)}`;
      default:
        return '';
    }
  };

  const qrData = generateQRData();

  // Update last QR code history when qrData changes
  useEffect(() => {
    if (qrData && qrData?.trim() !== '') {
      setLastQRCode({
        data: qrData,
        type: contentType,
        timestamp: new Date()?.toLocaleString(),
        imageUrl: `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(qrData)}&color=${foregroundColor?.replace('#', '')}&bgcolor=${backgroundColor?.replace('#', '')}`,
        settings: {
          size: qrSize,
          foregroundColor,
          backgroundColor,
          errorCorrection
        }
      });
    }
  }, [qrData, contentType, qrSize, foregroundColor, backgroundColor, errorCorrection]);

  const handleExport = async (type) => {
    const qrImageUrl = `https://api.qrserver.com/v1/create-qr-code/?size=${qrSize}x${qrSize}&data=${encodeURIComponent(qrData)}&color=${foregroundColor?.replace('#', '')}&bgcolor=${backgroundColor?.replace('#', '')}`;

    switch (type) {
      case 'download':
        const link = document.createElement('a');
        link.href = qrImageUrl;
        link.download = `qrcode-${Date.now()}.png`;
        document.body?.appendChild(link);
        link?.click();
        document.body?.removeChild(link);
        break;
      case 'copy':
        try {
          const response = await fetch(qrImageUrl);
          const blob = await response?.blob();
          await navigator.clipboard?.write([
            new ClipboardItem({ 'image/png': blob })
          ]);
        } catch (error) {
          console.error('Copy failed:', error);
        }
        break;
      case 'print':
        const printWindow = window.open('', '_blank');
        printWindow?.document?.write(`
          <html>
            <head>
              <title>Print QR Code</title>
              <style>
                body { margin: 0; display: flex; justify-content: center; align-items: center; min-height: 100vh; }
                img { max-width: 100%; height: auto; }
              </style>
            </head>
            <body>
              <img src="${qrImageUrl}" alt="QR Code" onload="window.print(); window.close();" />
            </body>
          </html>
        `);
        printWindow?.document?.close();
        break;
      default:
        break;
    }
  };

  const renderContentInput = () => {
    switch (contentType) {
      case 'text':
        return <TextInput value={textContent} onChange={setTextContent} />;
      case 'url':
        return <URLInput value={urlContent} onChange={setUrlContent} />;
      case 'wifi':
        return <WiFiInput value={wifiContent} onChange={setWifiContent} />;
      case 'contact':
        return <ContactInput value={contactContent} onChange={setContactContent} />;
      case 'email':
        return <EmailInput value={emailContent} onChange={setEmailContent} />;
      case 'sms':
        return <SMSInput value={smsContent} onChange={setSmsContent} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <SectionNavigator />
      <main className="pt-16 lg:pt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-12">
          <div className="mb-6 lg:mb-8">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground mb-2">
              QR Code Generator
            </h1>
            <p className="text-sm md:text-base text-muted-foreground font-body">
              Create professional QR codes for any purpose in seconds
            </p>
          </div>

          {/* How to Use Section - Moved Upwards */}
          <div className="mb-8 lg:mb-12 p-6 lg:p-8 rounded-xl bg-muted/30 border border-border">
            <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground mb-4">
              How to Use QRCodeGen
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {[
                {
                  step: '1',
                  title: 'Choose Type',
                  description: 'Select the content type you want to encode'
                },
                {
                  step: '2',
                  title: 'Enter Data',
                  description: 'Fill in your content details in the form'
                },
                {
                  step: '3',
                  title: 'Customize',
                  description: 'Adjust colors, size, and error correction'
                },
                {
                  step: '4',
                  title: 'Export',
                  description: 'Download, copy, or print your QR code'
                }
              ]?.map((item) => (
                <div key={item?.step} className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-accent text-accent-foreground flex items-center justify-center font-heading font-bold text-sm flex-shrink-0">
                    {item?.step}
                  </div>
                  <div>
                    <h3 className="text-sm font-heading font-semibold text-foreground mb-1">
                      {item?.title}
                    </h3>
                    <p className="text-xs font-caption text-muted-foreground leading-relaxed">
                      {item?.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
            <div className="lg:col-span-2 space-y-6">
              <section id="content" className="bg-card rounded-xl border border-border p-6 lg:p-8 shadow-warm-md scroll-mt-24">
                <ContentTypeSelector
                  selectedType={contentType}
                  onTypeChange={setContentType}
                />
              </section>

              <section id="input" className="bg-card rounded-xl border border-border p-6 lg:p-8 shadow-warm-md scroll-mt-24">
                {renderContentInput()}
              </section>

              <section id="customize" className="scroll-mt-24">
                <CustomizationPanel
                  isExpanded={customizationExpanded}
                  onToggle={setCustomizationExpanded}
                />
              </section>

              <section id="preview" className="lg:hidden scroll-mt-24">
                <QRPreview
                  qrData={qrData}
                  size={qrSize}
                  foregroundColor={foregroundColor}
                  backgroundColor={backgroundColor}
                />
              </section>
            </div>

            <div className="lg:col-span-1 space-y-6">
              <section className="hidden lg:block">
                <QRPreview
                  qrData={qrData}
                  size={qrSize}
                  foregroundColor={foregroundColor}
                  backgroundColor={backgroundColor}
                />
              </section>

              <section id="export" className="scroll-mt-24">
                <ExportActionBar
                  qrCodeData={qrData}
                  onExport={handleExport}
                />
              </section>

              {/* QR History Section - Last Generated QR Code */}
              {lastQRCode && (
                <section className="bg-card rounded-xl border border-border p-4 lg:p-6 shadow-warm-md">
                  <h3 className="text-sm md:text-base font-heading font-semibold text-foreground mb-3">
                    Last Generated QR Code
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-center p-3 bg-muted/30 rounded-lg">
                      <img
                        src={lastQRCode?.imageUrl}
                        alt="Last generated QR code"
                        className="w-32 h-32 object-contain"
                      />
                    </div>
                    <div className="space-y-1.5 text-xs">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground font-caption">Type:</span>
                        <span className="text-foreground font-medium capitalize">{lastQRCode?.type}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground font-caption">Generated:</span>
                        <span className="text-foreground font-medium">{lastQRCode?.timestamp}</span>
                      </div>
                      <div className="pt-2 border-t border-border">
                        <p className="text-muted-foreground font-caption mb-1">Content:</p>
                        <p className="text-foreground font-mono text-[10px] break-all line-clamp-3">
                          {lastQRCode?.data}
                        </p>
                      </div>
                    </div>
                  </div>
                </section>
              )}
            </div>
          </div>
        </div>
      </main>
      <footer className="border-t border-border bg-card mt-12 lg:mt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm font-caption text-muted-foreground">
              © {new Date()?.getFullYear()} QRCodeGen. All rights reserved.
            </p>
            <p className="text-xs font-caption text-muted-foreground">
              Professional QR Code Generator for Business & Personal Use
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default QRCodeGenerator;